import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { LockIcon, MailIcon, EyeIcon, EyeOffIcon, InfoIcon } from 'lucide-react';
const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDemoAccounts, setShowDemoAccounts] = useState(false);
  const {
    login
  } = useAdminAuth();
  const navigate = useNavigate();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate('/admin/dashboard');
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  const demoAccounts = [{
    role: 'Super Admin',
    email: 'admin@example.com',
    password: 'admin123'
  }, {
    role: 'Ministry of Health',
    email: 'health@example.com',
    password: 'health123'
  }, {
    role: 'Ministry of Education',
    email: 'education@example.com',
    password: 'education123'
  }, {
    role: 'Ministry of Finance',
    email: 'finance@example.com',
    password: 'finance123'
  }];
  const fillDemoCredentials = (email: string, password: string) => {
    setEmail(email);
    setPassword(password);
  };
  return <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4" data-id="element-1885">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-1886">
        <div className="bg-green-800 p-6 text-center" data-id="element-1887">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white mb-4" data-id="element-1888">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iLjN9cDQjVbDSGqwWquEQb/1000138367.png" alt="Edo State Logo" className="h-16 w-16" data-id="element-1889" />
          </div>
          <h1 className="text-2xl font-bold text-white" data-id="element-1890">Admin Portal</h1>
          <p className="text-green-200 text-sm mt-1" data-id="element-1891">Edo State Services</p>
        </div>
        <div className="p-6" data-id="element-1892">
          <h2 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-1893">Sign In</h2>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm" data-id="element-1894">
              {error}
            </div>}
          <form onSubmit={handleSubmit} data-id="element-1895">
            <div className="mb-4" data-id="element-1896">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1897">
                Email Address
              </label>
              <div className="relative" data-id="element-1898">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1899">
                  <MailIcon size={18} className="text-gray-400" data-id="element-1900" />
                </div>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@example.com" required className="pl-10 block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" data-id="element-1901" />
              </div>
            </div>
            <div className="mb-6" data-id="element-1902">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1903">
                Password
              </label>
              <div className="relative" data-id="element-1904">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1905">
                  <LockIcon size={18} className="text-gray-400" data-id="element-1906" />
                </div>
                <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required className="pl-10 block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500" data-id="element-1907" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-1908">
                  {showPassword ? <EyeOffIcon size={18} className="text-gray-400" data-id="element-1909" /> : <EyeIcon size={18} className="text-gray-400" data-id="element-1910" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={isLoading} className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1911">
              {isLoading ? <span className="flex items-center justify-center" data-id="element-1912">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1913">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1914"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1915"></path>
                  </svg>
                  Signing in...
                </span> : 'Sign In'}
            </button>
          </form>
          <div className="mt-6" data-id="element-1916">
            <button onClick={() => setShowDemoAccounts(!showDemoAccounts)} className="flex items-center text-sm text-green-600 hover:text-green-500" data-id="element-1917">
              <InfoIcon size={16} className="mr-1" data-id="element-1918" />
              {showDemoAccounts ? 'Hide demo accounts' : 'Show demo accounts'}
            </button>
            {showDemoAccounts && <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200" data-id="element-1919">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1920">
                  Demo Accounts:
                </h3>
                <div className="space-y-2" data-id="element-1921">
                  {demoAccounts.map((account, index) => <div key={index} className="flex flex-col" data-id="element-1922">
                      <div className="flex justify-between items-center" data-id="element-1923">
                        <span className="text-xs font-medium text-gray-600" data-id="element-1924">
                          {account.role}
                        </span>
                        <button onClick={() => fillDemoCredentials(account.email, account.password)} className="text-xs text-green-600 hover:underline" data-id="element-1925">
                          Use
                        </button>
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1926">
                        {account.email} / {account.password}
                      </div>
                    </div>)}
                </div>
                <p className="mt-2 text-xs text-gray-500" data-id="element-1927">
                  Click "Use" to automatically fill the login form with demo
                  credentials.
                </p>
              </div>}
          </div>
          <div className="mt-6 text-center" data-id="element-1928">
            <a href="/" className="text-sm text-green-600 hover:text-green-500" data-id="element-1929">
              Return to Main Portal
            </a>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-center text-xs text-gray-500" data-id="element-1930">
          Edo State Government © 2025. Powered by Edo ICT Agency.
        </div>
      </div>
    </div>;
};
export default AdminLogin;